import { DateTime } from 'luxon';
import { exportToExcel, type ColumnDefinition } from 'utilities/shared/exportToExcel.utility';
import displayToast from 'utilities/toast.utility';

type ExportableForm<T> = {
  values: {
    data: T[];
  };
};

type ExportAllResult<T> = {
  data: T[];
  total: number;
  error?: boolean;
};

export type RunTableExportParams<T extends Record<string, any>> = {
  all: boolean;
  form: ExportableForm<T>;
  handleExportAllData: () => Promise<ExportAllResult<T>>;
  exportColumns: ColumnDefinition[];
  fileNamePrefix: string;
  fileDateFormat?: string;
};

/**
 * Shared export handler for tables built on MantineTable + TableContext.
 *
 * When `all` is true, delegates to `handleExportAllData()` (snapshot of last
 * fetched dataset). Otherwise, exports only rows flagged as `selected` on
 * `form.values.data`. The resulting workbook name is `<fileNamePrefix>_DD-MM-YY`.
 * Status is surfaced via toast.
 *
 * @param params - {@link RunTableExportParams}
 */
export const runTableExport = async <T extends Record<string, any>>({
  all,
  form,
  handleExportAllData,
  exportColumns,
  fileNamePrefix,
  fileDateFormat = 'dd-LL-yy',
}: RunTableExportParams<T>): Promise<void> => {
  let exportItems: T[];
  if (all) {
    const { data, error } = await handleExportAllData();
    // A failed fetch (network/4xx/5xx) must not masquerade as "no data".
    if (error) {
      displayToast('No se pudieron obtener los datos para exportar. Intenta de nuevo.', 'error');
      return;
    }
    exportItems = data;
  } else {
    exportItems =
      form?.values?.data?.filter((item) => (item as { selected?: boolean }).selected) ?? [];
  }
  const exportFileName = `${fileNamePrefix}_${DateTime.now().toFormat(fileDateFormat)}`;
  const { status, message } = exportToExcel(exportColumns, exportItems, exportFileName);
  displayToast(message, status);
};
